<template>
  <div
    v-if="block && !block.hidden"
    :id="containerId"
    class="moveable-block-editor"
    :class="
      [isBlockSelecting ? 'active block-selecting' : 'inactive',
       {
         'changing': changing,
         'dragging-other-block': draggingBlockId && draggingBlockId !== block.id,
         'opacity-0': isPresentingDefaultContent,
         'border-gray': isDefaultContent,
         'is-dragging-group': isDraggingGroup,
       }
      ]
    "
  >
    <BlockEditor
      :component-data="componentData"
      :type="type"
      @ready="onBlockReady"
      @onBlur="onBlurBlock"
      @onFocusEditor="onFocusEditor"
      @update-content="onUpdateContent"
      v-click-outside="clickOutsideBlockConfig"
      :disabled="!moveable"
    />
    <Moveable
      v-if="moveable"
      className="moveable"
      :target="[`#${block.id}`]"
      :key="block.h + key"
      :verticalGuidelines="[640]"
      :horizontalGuidelines="[360]"
      :snappable="true"
      :isDisplaySnapDigit="true"
      :elementGuidelines="elementGuidelines"
      :snapGap="true"
      :snapDirections="snapDirections"
      :elementSnapDirections="snapDirections"
      :draggable="true"
      :edgeDraggable="true"
      :rotatable="!isMultipleBlocksSelecting"
      :rotationPosition="'bottom'"
      :resizable="!isMultipleBlocksSelecting"
      :origin="false"
      :throttleRotate="5"
      :throttleDrag="5"
      :throttleResize="2"
      :keepRatio="keepRatio"
      :renderDirections="renderDirections"
      @drag="onDrag"
      @dragStart="onDragStart"
      @dragEnd="onDragEnd"
      @rotate="onRotate"
      @rotateEnd="onRotateEnd"
      @resize="onResize"
      @resizeStart="onResizeStart"
      @resizeEnd="onResizeEnd"
      @click="onClick"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import Moveable from 'vue-moveable'
import { mapGetters, mapState } from 'vuex'
import store from '@/vuex/store'
import BlockEditor from '@/components-v2/presentation-editor/middle/editors/BlockEditor.vue'
import BlockActions from './BlockActions.vue'
import AIFloatButton from '@/components-v2/presentation-editor/new-slide/OpenAI/AIFloatButton.vue'
import utils from '@/util'
import transformUtils from '@/utils/transform.utils'
import eventCategories from '@/constant/eventCategories'
import EventBus from '@/eventBus'

const BlockActionsConstructor = Vue.extend(BlockActions)
const AIFloatButtonConstructor = Vue.extend(AIFloatButton)

export default {
  components: {
    BlockEditor,
    Moveable,
  },
  props: {
    block: {
      type: Object,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    slideId: {
      type: Number,
      default: null,
    },
    elementGuidelines: {
      type: Array,
      default: null,
    },
    isPresentingOrBackstageOrShare: {
      type: Boolean,
      default: false,
    },
    draggingBlockId: {
      type: String,
      default: null,
    },
    isBlockSelecting: {
      type: Boolean,
      default: false,
    },
    isMultipleBlocksSelecting: {
      type: Boolean,
      default: false,
    },
    selectingBlocks: {
      type: Array,
      default: null,
    },
    isDraggingGroup: {
      type: Boolean,
      default: false,
    },
    holdingShiftKey: {
      type: Boolean,
      default: false,
    },
    holdingCtrlKey: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      blockInstance: null,
      blockReady: false,
      isEditing: false,
      changing: false,
      key: Date.now(),
      countClick: 0,
      blockActionsMounted: false,
      AIFloatButtonMounted: false,
    }
  },

  computed: {
    ...mapState([
      'isDisableEditPresentation',
    ]),
    ...mapState({
      showHyperLink: (state) => state.presentation.showHyperLink,
      slideActive: (state) => state.slideActive,
    }),
    ...mapGetters(['isPresenting']),
    componentData() {
      return {
        ...this.block,
        slideId: this.slideId,
      }
    },
    snapDirections() {
      return {
        center: true, middle: true, left: true, right: true, top: true, bottom: true,
      }
    },
    containerId() {
      return `container-${this.block.id}`
    },
    moveable() {
      if (this.block.type === 'Image' &&
          this.block.content &&
          this.block.content.isPlaceholderImage
      ) {
        return false
      }
      return this.blockReady &&
        !this.isDisableEditPresentation &&
        !this.isPresentingOrBackstageOrShare
    },
    isDefaultContent() {
      return this.block.content &&
        this.block.content.value &&
        this.block.content.value.includes('Click to add')
    },
    isPresentingDefaultContent() {
      if (!this.block) return false
      if (this.block.type === 'Image') return false
      if (this.block.type === 'RichText') {
        return this.isPresentingOrBackstageOrShare &&
        this.isDefaultContent
      }
      return false
    },

    keepRatio() {
      if (this.type === 'Emoji') return true
      if (this.type === 'Image') return !this.holdingShiftKey
      return this.holdingShiftKey
    },
    renderDirections() {
      if (this.type === 'Emoji') return ['ne', 'nw', 'se', 'sw']
      if (this.type === 'Line') return ['e', 'w']
      return ['ne', 'nw', 'se', 'sw', 'n', 'e', 'w', 's']
    },

  },

  watch: {
    isBlockSelecting(value, oldValue) {
      if (value && value !== oldValue) {
        this.mountBlockActions()
        this.mountAIFloatButton()
        this.countClick = 1
        return
      }
      if (!value && value !== oldValue) {
        if (this.blockInstance) {
          this.blockInstance.blur()
        }
        EventBus.$emit('show-freestyle-editor-toolbar', false)
        this.countClick = 0
        this.unmountBlockActions()
        this.unmountAIFloatButton()
      }
    },
    isEditing(val, oldval) {
      if (this.type === 'RichText' && val && val !== oldval) {
        this.unmountBlockActions()
      }
    },
    changing(val, oldval) {
      if (val && val !== oldval) {
        this.unmountBlockActions()
        this.unmountAIFloatButton()
      }
      if (!val && val !== oldval && this.isBlockSelecting) {
        this.mountBlockActions()
        this.unmountAIFloatButton()
      }
    },
    isMultipleBlocksSelecting(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.unmountBlockActions()
        this.unmountAIFloatButton()
      }
    },
    'block.h': function watchBlockHash(newVal, oldVal) {
      if (newVal === oldVal) return
      this.unmountBlockActions()
      this.unmountAIFloatButton()
      if (this.isBlockSelecting && !this.isEditing) {
        this.mountBlockActions()
        this.mountAIFloatButton()
      }
    },
  },

  created() {
    this.clickOutsideBlockConfig = {
      handler: this.onClickOutside,
      middleware: this.clickOutsideMiddleware,
    }
  },

  mounted() {
    EventBus.$on(`update-moveable-control-${this.block.id}`, () => {
      this.key = Date.now()
    })
  },
  destroyed() {
    this.blockReady = false
    this.blockActionsMounted = false
    this.AIFloatButtonMounted = false
    EventBus.$off(`update-moveable-control-${this.block.id}`)
  },
  methods: {
    onBlockReady() {
      this.blockInstance = document.getElementById(this.block.id)
      this.$nextTick(() => {
        this.blockReady = true
      })
    },

    // Mount the block action to the top-left resize handler, so that it will stick there
    // regardless of the position of the block
    mountBlockActions() {
      if (this.isMultipleBlocksSelecting) return
      const self = this
      setTimeout(() => {
        const isLineBlock = this.block.type === 'Line'
        const topLeftResizeHandler = isLineBlock ?
          document.querySelector(`#${this.containerId} .moveable-control.moveable-direction.moveable-w`) : document.querySelector(`#${this.containerId} .moveable-control.moveable-direction.moveable-nw`)

        if (topLeftResizeHandler) {
          const blockActionWrapper = document.createElement('div')
          topLeftResizeHandler.appendChild(blockActionWrapper)
          const BlockActionsInstance = new BlockActionsConstructor({
            propsData: {
              onClickDuplicateBlock: self.onClickDuplicateBlock,
              onClickDeleteBlock: self.onClickDeleteBlock,
              onClickEditImageBlock: self.onClickEditImageBlock,
              onClickOpenContextMenuBlock: self.onClickOpenContextMenuBlock,
              blockData: self.block,
            },
          })
          BlockActionsInstance.$mount(blockActionWrapper)
          this.blockActionsMounted = true
        }
      }, 20)
    },
    unmountBlockActions() {
      setTimeout(() => {
        const isLineBlock = this.block.type === 'Line'
        const topLeftResizeHandler = isLineBlock ?
          document.querySelector(`#${this.containerId} .moveable-control.moveable-direction.moveable-w`) : document.querySelector(`#${this.containerId} .moveable-control.moveable-direction.moveable-nw`)
        if (topLeftResizeHandler) {
          topLeftResizeHandler.innerHTML = ''
          this.blockActionsMounted = false
        }

        // Unmount the tooltip as well
        const blockActionsTooltips = document.querySelectorAll('.tooltip.block-action-tooltip')
        if (blockActionsTooltips && blockActionsTooltips.length) {
          blockActionsTooltips.forEach((tooltip) => {
            tooltip.innerHTML = ''
          })
        }
      }, 20)
    },
    mountAIFloatButton() {
      if (this.isMultipleBlocksSelecting || this.isEURegion) return
      if (!this.block || !this.block.type || this.block.type !== 'RichText') return
      setTimeout(() => {
        const isLineBlock = this.block.type === 'Line'
        const bottomRightResizeHandler = isLineBlock ?
          document.querySelector(`#${this.containerId} .moveable-control.moveable-direction.moveable-e`) : document.querySelector(`#${this.containerId} .moveable-control.moveable-direction.moveable-se`)

        if (bottomRightResizeHandler) {
          const AIFloatButtonWrapper = document.createElement('div')
          bottomRightResizeHandler.appendChild(AIFloatButtonWrapper)
          const AIFloatButtonInstance = new AIFloatButtonConstructor({
            store,
            propsData: {
              isContentSlide: true,
              isButtonVisible: true,
            },
          })
          AIFloatButtonInstance.$mount(AIFloatButtonWrapper)
          this.AIFloatButtonMounted = true
        }
      }, 20)
    },
    unmountAIFloatButton() {
      setTimeout(() => {
        const isLineBlock = this.block.type === 'Line'
        const bottomRightResizeHandler = isLineBlock ?
          document.querySelector(`#${this.containerId} .moveable-control.moveable-direction.moveable-e`) : document.querySelector(`#${this.containerId} .moveable-control.moveable-direction.moveable-se`)
        if (bottomRightResizeHandler) {
          bottomRightResizeHandler.innerHTML = ''
          this.AIFloatButtonMounted = false
        }
      }, 20)
    },
    onFocusEditor() {
      this.isEditing = true
      this.$emit('focusEditor', this.block.id)
    },
    onBlurBlock() {
      if (this.holdingCtrlKey || this.holdingShiftKey) return
      if (this.isBlockSelecting) {
        this.unmountBlockActions()
        this.countClick = 0
        this.isEditing = false
        this.$emit('deselectBlock', this.block.id)
      }
    },
    onClickOutside() {
      this.onBlurBlock()
    },
    clickOutsideMiddleware(e) {
      if (this.block.type === 'Line') {
        return !e.target.closest('.line-toolbar-wrapper') &&
          !e.target.closest('.pcr-app.visible')
      }

      if (this.block.type === 'Shape') {
        return !e.target.closest('.shape-toolbar-wrapper') &&
          !e.target.closest('.pcr-app.visible')
      }

      return !e.target.closest('.rich-text-editor') &&
        !e.target.closest('.tox-dialog') &&
        !e.target.closest('.content-right-click-menu')
    },

    onDrag(event) {
      // Khi drag sẽ không tính là click, nên sẽ phải set lượt click = 1
      try {
        this.countClick = 1
        this.changing = true
        // const [moveDistanceX, moveDistanceY] = event.dist
        // if (Math.abs(moveDistanceX) > 5 || Math.abs(moveDistanceY) > 5) {
        this.blockInstance.style.transform = event.transform
        // }
        if (!this.isBlockSelecting) {
          this.$emit('selectBlock', this.block)
        }
      } catch (e) {
        // do nothing
      }
    },
    onDragStart() {
      this.previousTransform = this.blockInstance.style.transform

      this.$emit('drag-start', this.block)
      // Tránh trường hợp bị duplicate cả ở Side Left
      EventBus.$emit('dragBlockContentSlide')
    },
    onDragEnd(e) {
      const { isDrag, target } = e
      if (transformUtils.isBlockNotInCanvas(e.target.id)) {
        this.blockInstance.style.transform = this.previousTransform
        this.previousTransform = null

        return
      }
      if (isDrag) {
        this.updateBlockStyle({
          transform: target.style.transform,
        }, 'Drag')
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Move canvas block',
          null,
          null,
          {
            slideId: this.slideId,
            isImportedFromPPT: this.block.content && this.block.content.isImportedFromPPT,
          },
        )
      }
      this.changing = false

      this.$emit('drag-end')
    },

    onRotate({ drag }) {
      this.blockInstance.style.transform = drag.transform
    },
    onRotateEnd({ target, isDrag }) {
      if (isDrag) {
        this.updateBlockStyle({
          transform: target.style.transform,
        }, 'Rotate')
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Rotate block success',
          this.$store.state.slideActive.id,
        )
      }
      this.changing = false
    },
    onResize(data) {
      const {
        width, height, drag,
      } = data
      this.blockInstance.style.transform = drag.transform
      this.blockInstance.style.width = `${width}px`
      this.blockInstance.style.height = `${height}px`
    },
    onResizeStart() {
      this.countClick = 1
      this.previousTransform = this.blockInstance.style.transform
      this.$emit('drag-start', this.block)
    },
    onResizeEnd(e) {
      const { isDrag, target } = e
      if (transformUtils.isBlockNotInCanvas(e.target.id)) {
        this.blockInstance.style.transform = this.previousTransform
        this.previousTransform = null
        return
      }
      if (isDrag) {
        this.updateBlockStyle({
          width: target.style.width,
          height: target.style.height,
          transform: target.style.transform,
        }, 'Resize')
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Resize block success',
          null,
          null,
          {
            slideId: this.slideId,
            isImportedFromPPT: this.block.content && this.block.content.isImportedFromPPT,
          },
        )
      }
      this.changing = false
    },

    onUpdateContent(contentUpdate) {
      this.updateBlockContent(contentUpdate)
    },
    onClick() {
      if (!this.isMultipleBlocksSelecting) {
        this.countClick++
        if (this.countClick >= 2 && this.block.type === 'RichText') {
          EventBus.$emit('show-freestyle-editor-toolbar', true)
          this.blockInstance.dispatchEvent(new Event('click'))
        }
      }

      this.$emit('selectBlock', this.block)

      this.toggleButtonAddLinkPluginTextEditor()
    },
    toggleButtonAddLinkPluginTextEditor() {
      setTimeout(() => {
        if (document.querySelector('#freestyle-editor-toolbar button[title="Insert/edit link"]')) {
          if (!this.showHyperLink) {
            document.querySelector('#freestyle-editor-toolbar button[title="Insert/edit link"]').classList.add('disabled')
          } else {
            document.querySelector('#freestyle-editor-toolbar button[title="Insert/edit link"]').classList.remove('disabled')
          }
        }
      }, 50)
    },
    onClickDuplicateBlock() {
      this.$emit('duplicateBlock', [this.block])
      utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Duplicate canvas blocks', 'Duplicate icon', null, {
        slideId: this.slideId,
      })
    },
    onClickDeleteBlock() {
      this.$emit('deleteBlock', [this.block])
      if (this.block.type === 'RichText') {
        EventBus.$emit('show-freestyle-editor-toolbar', false)
      }

      utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Delete canvas blocks', 'Delete icon', null, {
        slideId: this.slideId,
        isImportedFromPPT: this.block.content && this.block.content.isImportedFromPPT,
      })
    },
    onClickEditImageBlock() {
      this.$emit('editImageBlock', this.block.id, this.block.style)
      utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Change image block', 'Change image icon', null, {
        slideId: this.slideId,
      })
    },
    onClickOpenContextMenuBlock(event) {
      this.$emit('openContextMenuBlock', event, 'menu-icon')
    },

    updateBlockStyle(blockData, type) {
      this.$emit('update-style', this.block.id, blockData, type)
    },
    updateBlockContent(blockData) {
      this.$emit('update-content', this.block.id, blockData)
    },
  },
}
</script>

<style lang="scss" scoped>
.moveable-block-editor {
  .element-editor{
    position: absolute;
    font-size: 2em;
  }
  &.inactive .element-editor:hover {
    cursor: default;
  }
  &.changing .element-editor:hover {
    cursor: move;
  }

  &.inactive::v-deep {
    .moveable-line, .moveable-control {
      background: transparent !important;
      border-color: transparent !important;
    }
  }

  // &.active::v-deep {
  //   .moveable-line:not(.moveable-rotation-line) {
  //     background: #4af !important;
  //     height: 2px !important;
  //   }

  // }
  &:focus::v-deep {
      .moveable-line, .moveable-control {
        background: #4af !important;
        border-color: #4af !important;
      }
  }

  &.active:not(.changing)::v-deep {
    .moveable-line:hover, .moveable-padding:hover{
      cursor: move;
    }
  }

  &.changing:hover::v-deep {
    .moveable-line:not(.moveable-rotation-line) {
        background: #4af !important;
      }
  }
  &.border-gray:not(.active)::v-deep {
    .moveable-line:not(.moveable-rotation-line) {
        background: #ccc !important;
      }
  }

  &:not(.dragging-other-block):not(.active):hover::v-deep {
    .moveable-line:not(.moveable-rotation-line) {
      background: #4af !important;
    }
  }

  // &.active.changing::v-deep {
  //   .moveable-line.moveable-dashed {
  //     box-sizing: border-box;
  //     background: transparent !important;
  //     &.moveable-horizontal {
  //       border-top: 1px dashed rgb(92, 201, 251);
  //     }
  //     &.moveable-vertical {
  //       border-left: 1px dashed rgb(92, 201, 251);
  //     }
  //   }
  // }

  &::v-deep .rich-text-editor {
    overflow: hidden !important;
  }
}

</style>
